import { h, mount } from "@/lib/dom";
import { icon, type IconName } from "@/lib/icons";
import { button, openModal, toast } from "@/components/ui";
import { presetPicker, createPresetSubjects, type PresetPicker } from "@/features/subjects/presets";
import { getTheme, setTheme } from "@/lib/theme";
import type { Theme } from "@/types/models";

/**
 * Onboarding beim ersten Login: kurze Tour + Grundeinstellungen + Fächer-
 * Schnellstart. Die fertigen Fächer (Name, Icon, Farbe, Kategorie) kommen aus
 * `features/subjects/presets` – dieselbe Vorlage ist später in den
 * Einstellungen erneut nutzbar.
 */

const DONE_KEY = "onboarding_done_v1";
let shownThisSession = false;

const FEATURES: { ic: IconName; title: string; text: string }[] = [
  { ic: "folder", title: "Fächer & Themen", text: "Jede Klausur ist ein Thema mit Termin – die App sortiert nach Dringlichkeit." },
  { ic: "pencil", title: "Mitschrift & Lernzettel", text: "Markdown-Editor mit Tabellen, Formeln, Karteikarten und PDF-Export." },
  { ic: "target", title: "Üben mit KI", text: "Quiz, Karteikarten (FSRS), Probeklausuren mit Benotung – aus deinen Notizen." },
  { ic: "bar-chart", title: "Statistik & Streak", text: "Heatmap, Wochenbilanz und dein Fehler-Deck zeigen, wo du stehst." },
];

/** Beim ersten Besuch (keine Fächer, noch nie gezeigt) automatisch öffnen. */
export function maybeShowOnboarding(opts: { hasSubjects: boolean; onChanged: () => void }): void {
  if (shownThisSession) return;
  try {
    if (localStorage.getItem(DONE_KEY)) return;
    if (opts.hasSubjects) {
      // Bestandsnutzer: nicht nerven, als erledigt markieren.
      localStorage.setItem(DONE_KEY, "1");
      return;
    }
  } catch { return; }
  shownThisSession = true;
  openOnboarding(opts.onChanged);
}

export function openOnboarding(onChanged: () => void): void {
  let step = 0;
  const body = h("div", null);

  const markDone = () => {
    try { localStorage.setItem(DONE_KEY, "1"); } catch { /* ignore */ }
  };

  /* ---- Schritt 1: Willkommen + Theme ---- */
  function themeButton(theme: Theme, label: string, ic: IconName): HTMLElement {
    const b = h("button", {
      class: "ob-theme-btn" + (getTheme() === theme ? " active" : ""),
      type: "button",
      onclick: () => {
        setTheme(theme);
        b.parentElement?.querySelectorAll(".ob-theme-btn").forEach((x) => x.classList.remove("active"));
        b.classList.add("active");
      },
    }, h("span", { class: "row", html: icon(ic, 20) }), label);
    return b;
  }

  function stepWelcome(): HTMLElement {
    return h("div", null,
      h("div", { class: "ob-hero" },
        h("div", { class: "ob-hero-icon" }, "📚"),
        h("h3", null, "Willkommen!"),
        h("p", { class: "muted" }, "Deine Lernapp für Mitschriften, Lernzettel und KI-gestütztes Üben. Kurz einrichten – dann kann es losgehen.")
      ),
      h("div", { class: "ob-features" },
        ...FEATURES.map((f) => h("div", { class: "ob-feature" },
          h("span", { class: "row", html: icon(f.ic, 18) }),
          h("div", null, h("b", null, f.title), h("span", { class: "muted" }, f.text))
        ))
      ),
      h("p", { class: "muted text-sm", style: "text-align:center;margin-top:16px" }, "Wie soll die App aussehen?"),
      h("div", { class: "ob-theme-row" },
        themeButton("light", "Hell", "sun"),
        themeButton("dark", "Dunkel", "moon")
      )
    );
  }

  /* ---- Schritt 2: Sortierung + Fächer per Checkbox ---- */
  // Der Picker wird einmalig gebaut, damit die Auswahl beim Blättern bleibt.
  let picker: PresetPicker | null = null;

  function stepSubjects(): HTMLElement {
    picker ??= presetPicker({ onChange: updateNextLabel });
    return h("div", null,
      h("p", { class: "muted", style: "margin-bottom:10px" },
        "Welche Fächer hast du? Sie werden fertig eingerichtet (Icon, Farbe, Überschrift). Wähle, wie sie gruppiert werden:"),
      picker.el
    );
  }

  /* ---- Navigation ---- */
  const dots = [0, 1].map((i) => h("span", { class: "ob-step-dot" + (i === 0 ? " active" : "") }));
  const backBtn = button({ label: "Zurück", variant: "ghost", onClick: () => show(step - 1) });
  const nextBtn = button({ label: "Weiter", variant: "primary", onClick: () => void next() });
  const skipBtn = button({
    label: "Überspringen", variant: "ghost",
    onClick: () => { markDone(); handle.close(); },
  });

  async function next() {
    if (step === 0) { show(1); return; }
    // Fertig: gewählte Fächer anlegen
    nextBtn.disabled = true;
    nextBtn.classList.add("loading");
    const created = picker ? await createPresetSubjects(picker) : 0;
    markDone();
    handle.close();
    if (created) toast(`${created} ${created === 1 ? "Fach" : "Fächer"} angelegt – viel Erfolg!`, "success");
    onChanged();
  }

  /** Beschriftung der Primär-Aktion an die Auswahl anpassen. */
  function updateNextLabel(count = picker?.getSelected().length ?? 0) {
    if (step !== 1) return;
    nextBtn.textContent = count
      ? `${count} ${count === 1 ? "Fach" : "Fächer"} anlegen`
      : "Ohne Fächer starten";
  }

  function show(i: number) {
    step = Math.max(0, Math.min(1, i));
    dots.forEach((d, j) => d.classList.toggle("active", j === step));
    backBtn.style.display = step === 0 ? "none" : "";
    mount(body, h("div", null,
      h("div", { class: "ob-steps" }, ...dots),
      step === 0 ? stepWelcome() : stepSubjects()
    ));
    if (step === 0) nextBtn.textContent = "Weiter";
    else updateNextLabel();
  }

  const handle = openModal({
    title: "Erste Schritte",
    wide: true,
    body,
    footer: h("div", { class: "row", style: "width:100%" },
      skipBtn,
      h("span", { class: "spacer" }),
      backBtn,
      nextBtn
    ),
    onClose: markDone,
  });

  show(0);
}
